import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createElement, useMemo, } from "react";
import { usePermissions } from "./use-permissions.js";
/**
 * Filters `groups` according to the user's permissions. An item is visible
 * when (a) `requires` is satisfied, or (b) `requires` is omitted and
 * `canViewRoute(path)` is true (route-resolved via pages_registry).
 *
 * Returned groups never contain hidden items, and empty groups are dropped.
 */
export function useFilteredNav(groups) {
    const { can, canViewRoute } = usePermissions();
    return useMemo(() => {
        return groups
            .map((g) => ({
            ...g,
            items: g.items.filter((item) => {
                if (item.requires) {
                    return can(item.requires.resource, item.requires.action);
                }
                return canViewRoute(item.path);
            }),
        }))
            .filter((g) => g.items.length > 0);
    }, [groups, can, canViewRoute]);
}
/**
 * Minimal declarative sidebar. The host app may instead use
 * `useFilteredNav()` and render its own structure.
 *
 * Inline structural styles only — visual styling is done by the host via
 * `className` / `itemClassName` / `activeItemClassName` props. Shared UI
 * packages must ship inline structural styles per repo convention.
 */
export function Sidebar({ groups, currentPath, linkComponent, className, groupClassName, itemClassName, activeItemClassName, }) {
    const filtered = useFilteredNav(groups);
    const Link = linkComponent;
    return (_jsx("nav", { className: className, style: { display: "flex", flexDirection: "column", gap: 12 }, children: filtered.map((g, gi) => (_jsxs("div", { className: groupClassName, style: { display: "flex", flexDirection: "column", gap: 4 }, children: [g.label ? _jsx(SidebarGroupLabel, { label: g.label }) : null, _jsx("ul", { style: { listStyle: "none", margin: 0, padding: 0 }, children: g.items.map((item) => (_jsx(SidebarItem, { item: item, active: item.path === currentPath, Link: Link, itemClassName: itemClassName, activeItemClassName: activeItemClassName }, item.path))) })] }, g.label ?? `group-${gi}`))) }));
}
function SidebarGroupLabel({ label }) {
    return (_jsx("div", { style: {
            fontSize: 11,
            textTransform: "uppercase",
            opacity: 0.6,
            letterSpacing: 0.5,
            padding: "4px 12px",
        }, children: label }));
}
function SidebarItem({ item, active, Link, itemClassName, activeItemClassName, }) {
    const cls = [itemClassName, active ? activeItemClassName : null]
        .filter(Boolean)
        .join(" ");
    const content = (_jsxs(_Fragment, { children: [item.icon ? (_jsx("span", { style: { display: "inline-flex", marginRight: 8 }, children: item.icon })) : null, item.label] }));
    const props = {
        href: item.path,
        className: cls || undefined,
        style: {
            display: "flex",
            alignItems: "center",
            padding: "6px 12px",
            textDecoration: "none",
            color: "inherit",
        },
        children: content,
    };
    return (_jsx("li", { children: Link
            ? createElement(Link, props)
            : createElement("a", props) }));
}
//# sourceMappingURL=sidebar.js.map